/**
 * ScreenshotFloatingButton - 접이식 플로팅 캡처 버튼
 *
 * 캡처 흐름: 캡처 → 바로 피드백 폼(썸네일 첨부) → 폼에서 클릭하면 마스킹 편집
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Scissors, Smartphone, Archive } from 'lucide-react';
import { screenCapture } from '../../services/ScreenCaptureService';
import { FeedbackFormModal } from './FeedbackFormModal';
import { ScreenshotCapture } from './ScreenshotCapture';
import { Capacitor } from '@capacitor/core';
import { t } from '@lingui/macro';
import { Switch } from '../../components/ui/switch';

interface Props {
  onClose: () => void;
  draftCount?: number;
  onDraftsOpen?: () => void;
  onTriggerCapture?: () => void;
  onTriggerAreaCapture?: () => void;
}

type CapturePhase = 'idle' | 'area-select' | 'form';

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
const isNative = Capacitor.isNativePlatform();

export const ScreenshotFloatingButton: React.FC<Props> = ({ onClose, draftCount = 0, onDraftsOpen, onTriggerCapture, onTriggerAreaCapture }) => {
  const [posY, setPosY] = useState(80);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandOffset, setExpandOffset] = useState(0);
  const [panelWidth, setPanelWidth] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isDraggingRef = useRef(false);
  const dragModeRef = useRef<'none' | 'vertical' | 'horizontal'>('none');
  const dragStartRef = useRef<{
    x: number; y: number;
    startY: number; startExpand: number;
  } | null>(null);

  useEffect(() => {
    if (panelRef.current) {
      setPanelWidth(panelRef.current.offsetWidth);
    }
  }, []);

  const [shakeEnabled, setShakeEnabled] = useState(false);

  // ── 즉시 캡처 → 바로 폼 ──
  const handleInstantCapture = useCallback(async () => {
    if (onTriggerCapture) onTriggerCapture();
  }, [onTriggerCapture]);

  // ── 영역 선택 ──
  const handleAreaSelect = useCallback(async () => {
    if (onTriggerAreaCapture) onTriggerAreaCapture();
  }, [onTriggerAreaCapture]);

  // ── 드래그 ──
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    isDraggingRef.current = false;
    dragModeRef.current = 'none';
    dragStartRef.current = { x: e.clientX, y: e.clientY, startY: posY, startExpand: expandOffset };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [posY, expandOffset]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStartRef.current || panelWidth === 0) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    if (dragModeRef.current === 'none' && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      dragModeRef.current = Math.abs(dy) > Math.abs(dx) ? 'vertical' : 'horizontal';
      isDraggingRef.current = true;
    }
    if (dragModeRef.current === 'vertical') {
      setPosY(Math.max(20, Math.min(window.innerHeight - 60, dragStartRef.current.startY + dy)));
    } else if (dragModeRef.current === 'horizontal') {
      setExpandOffset(Math.max(0, Math.min(panelWidth, dragStartRef.current.startExpand - dx)));
    }
  }, [panelWidth]);

  const handlePointerUp = useCallback(() => {
    dragStartRef.current = null;
    if (dragModeRef.current === 'horizontal' || !isDraggingRef.current) {
      if (expandOffset > panelWidth / 2) { setExpandOffset(panelWidth); setIsExpanded(true); }
      else { setExpandOffset(0); setIsExpanded(false); }
    }
    dragModeRef.current = 'none';
    setTimeout(() => { isDraggingRef.current = false; }, 50);
  }, [expandOffset, panelWidth]);

  const handleTabClick = useCallback(() => {
    if (isDraggingRef.current) return;
    if (isExpanded) { setExpandOffset(0); setIsExpanded(false); }
    else { setExpandOffset(panelWidth); setIsExpanded(true); }
  }, [isExpanded, panelWidth]);

  useEffect(() => {
    const handleOutsideClick = (e: PointerEvent) => {
      if (isExpanded && wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setExpandOffset(0);
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('pointerdown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [isExpanded]);

  const translateX = panelWidth === 0 ? 999 : panelWidth - expandOffset;

  return (
    <>
        <div
          ref={wrapperRef}
          data-screenshot-exclude
          style={{
            position: 'fixed', right: 0, top: posY,
            transform: `translateX(${translateX}px)`,
            zIndex: 99999,
            visibility: panelWidth === 0 ? 'hidden' : 'visible',
            transition: dragStartRef.current ? 'none' : 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
          className="flex items-stretch"
        >
          {/* 손잡이 탭 */}
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onClick={handleTabClick}
            className="flex items-center justify-center w-[5px] rounded-l-xl cursor-grab active:cursor-grabbing select-none touch-none hover:w-2.5 hover:-ml-[5px] transition-all bg-primary"
            style={{
              boxShadow: '-2px 0 8px var(--primary-alpha)',
            }}
          >
            <span className="text-white text-meta-sm font-bold py-2"></span>
          </div>

          {/* 버튼 패널 */}
          <div ref={panelRef} className="flex items-center gap-1 px-2 py-1.5 bg-card border border-l-0 border-border shadow-xl rounded-r-lg">
            <button
              onClick={() => !isDraggingRef.current && handleInstantCapture()}
              className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-primary/10 text-primary transition-colors"
              title={t`Instant Capture`}
            >
              <Camera className="w-4 h-4" />
              {!isNative && (
                <span className="text-meta-sm text-muted-foreground font-mono leading-none">{isMac ? '⌘⇧K' : '⇧K'}</span>
              )}
            </button>
            {!isNative && (
              <button
                onClick={() => !isDraggingRef.current && handleAreaSelect()}
                className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                title={t`Area Select` + ` (${isMac ? '⌘' : 'Ctrl'}+Shift+A)`}
              >
                <Scissors className="w-4 h-4" />
                <span className="text-meta-sm text-muted-foreground font-mono leading-none">{isMac ? '⌘⇧A' : '⇧A'}</span>
              </button>
            )}
            {isNative && (
              <button
                onClick={async () => {
                  if (isDraggingRef.current) return;

                  if (shakeEnabled) {
                    // OFF
                    setShakeEnabled(false);
                    window.dispatchEvent(new Event('shake-disabled'));
                    return;
                  }

                  // ON - iOS 권한 요청 (사용자 터치 컨텍스트)
                  const DME = DeviceMotionEvent as any;
                  if (typeof DME.requestPermission === 'function') {
                    try {
                      const permission = await DME.requestPermission();
                      if (permission === 'granted') {
                        setShakeEnabled(true);
                        window.dispatchEvent(new Event('motion-permission-granted'));
                        window.dispatchEvent(new Event('shake-enabled'));
                      }
                    } catch (err) {
                      console.warn('Motion permission denied:', err);
                    }
                  } else {
                    // Android: 바로 활성화
                    setShakeEnabled(true);
                    window.dispatchEvent(new Event('shake-enabled'));
                  }
                }}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-lg transition-colors ${
                  shakeEnabled
                    ? 'text-badge-success'
                    : 'text-muted-foreground hover:bg-primary/10'
                }`}
                title={shakeEnabled ? t`Disable shake capture` : t`Enable shake capture`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span className="text-meta-sm leading-none whitespace-nowrap">
                  {shakeEnabled ? t`Shake ON` : t`Shake`}
                </span>
              </button>
            )}
            {/* 드래프트 목록 */}
            {onDraftsOpen && (
              <button
                onClick={() => !isDraggingRef.current && onDraftsOpen()}
                className="relative flex items-center gap-1 px-1.5 py-1 rounded-lg text-muted-foreground hover:bg-primary/10 transition-colors"
                title={t`Saved captures`}
              >
                <Archive className="w-3.5 h-3.5" />
                {draftCount > 0 && <span className="text-meta-sm leading-none whitespace-nowrap">{t`Saved`}</span>}
                {draftCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-3.5 h-3.5 flex items-center justify-center rounded-full bg-orange-500 text-white text-meta-sm font-bold leading-none px-0.5">
                    {draftCount}
                  </span>
                )}
              </button>
            )}
            <div className="w-px h-7 bg-border mx-1" />
            <div className="flex flex-col items-center justify-center gap-0.5 px-2 mb-0.5 cursor-pointer" onClick={() => !isDraggingRef.current && onClose()}>
              <Switch
                checked={true}
                onCheckedChange={() => !isDraggingRef.current && onClose()}
                className="scale-75 cursor-pointer"
                title={t`Turn off debug mode`}
              />
              <span className="text-meta-sm text-muted-foreground leading-none font-medium">Debug</span>
            </div>
          </div>
        </div>
    </>
  );
};
